/**
 * Input/output contracts for the SharePoint headshot-library callables
 * (ADR-178).
 *
 * The Headshot Library is an EXTERNAL standalone system — a SharePoint
 * document library whose conventions live in the frg-headshot-library repo.
 * The app is a pure READ consumer: `listSharePointHeadshots` proposes
 * matches between library files and persons, an organizer reviews them in
 * the admin dialog, and `importSharePointHeadshots` downloads only the
 * approved files and routes them through the app's existing photo write
 * path (Firebase Storage → `persons.photoURL`). The app never writes to
 * the library; files that can't be matched are reported with a pointer to
 * the library's own "Needs identification" view, where reconciliation
 * natively lives.
 *
 * Authorization (both callables): global organizer or admin.
 */

import { z } from 'zod';

/** How a library file was matched to a person. HubSpotID is the library's upsert key (ADR-179). */
export type HeadshotMatchSource = 'hubspotid' | 'email' | 'repcrd';

/**
 * One library file, with its metadata columns and (when resolvable) the
 * person it matches. `thumbnailUrl` is a pre-authenticated, short-lived
 * (~1 h) Graph URL — display-only, never persisted. `match` is null for
 * files that need identification; `conflict` is set when email and CRD
 * point at different persons (surfaced, never auto-resolved).
 */
export interface HeadshotProposal {
  driveItemId: string;
  fileName: string;
  /** Bytes, from the driveItem. */
  size: number;
  mimeType: string | null;
  /** `Email` column (lowercased; dev library: `PersonEmail`). */
  email: string | null;
  /** `CRDContact` column (dev library: `RepCRD`). */
  repCrd: string | null;
  /** `HubSpotID` column — the production library's upsert key. */
  hubspotId: string | null;
  /** Display name from the library row. */
  sharePointDisplayName: string | null;
  thumbnailUrl: string | null;
  match: {
    personId: string;
    personDisplayName: string;
    personEmail: string;
    matchedBy: HeadshotMatchSource;
    /** True when the person already has a photo — the dialog default-unchecks these. */
    hasExistingPhoto: boolean;
  } | null;
  conflict: string | null;
}

// ── listSharePointHeadshots ──

export const listSharePointHeadshotsInputSchema = z.object({}).strict();

export type ListSharePointHeadshotsInput = z.infer<
  typeof listSharePointHeadshotsInputSchema
>;

export interface ListSharePointHeadshotsResponse {
  success: true;
  /** Matched and conflicted files only — unmatched files are summarized by count. */
  proposals: HeadshotProposal[];
  /** Total image files seen in the library. */
  totalFiles: number;
  /**
   * Files matching nobody in this app. Expected to be large in production:
   * the library covers FRG's full contact sync, not just app users.
   */
  unmatchedCount: number;
  /** Deep link to the library for organizers. */
  libraryUrl: string;
}

// ── getLibraryHeadshotForContact ──

/**
 * Link-time lookup: given a HubSpot contact id (the library's upsert key),
 * return its library photo if one exists, for staging in the person edit
 * form (review-first — nothing persists until Save, ADR-172 model).
 */
export const getLibraryHeadshotForContactInputSchema = z
  .object({
    contactId: z.string().min(1).max(64),
  })
  .strict();

export type GetLibraryHeadshotForContactInput = z.infer<
  typeof getLibraryHeadshotForContactInputSchema
>;

export interface GetLibraryHeadshotForContactResponse {
  success: true;
  headshot: {
    driveItemId: string;
    fileName: string;
    /** Short-lived (~1 h) pre-authenticated thumbnail URL — display only. */
    thumbnailUrl: string | null;
  } | null;
}

// ── importSharePointHeadshots ──

export const importSharePointHeadshotsInputSchema = z
  .object({
    items: z
      .array(
        z
          .object({
            driveItemId: z.string().min(1),
            personId: z.string().min(1),
          })
          .strict(),
      )
      .min(1)
      .max(100),
  })
  .strict();

export type ImportSharePointHeadshotsInput = z.infer<
  typeof importSharePointHeadshotsInputSchema
>;

export type HeadshotImportStatus =
  | 'imported'
  | 'skipped-mismatch'
  | 'skipped-too-large'
  | 'skipped-unsupported-type'
  | 'failed';

export interface HeadshotImportResult {
  driveItemId: string;
  personId: string;
  status: HeadshotImportStatus;
  photoURL: string | null;
  message: string | null;
  /**
   * True when this import ALSO linked the person to the file's HubSpot
   * contact (ADR-181 amendment): an approved import for an unlinked person
   * stamps `personInternals.hubspotContactId` from the library row. An
   * existing link to a different contact is never overwritten.
   */
  linked?: boolean;
}

export interface ImportSharePointHeadshotsResponse {
  success: true;
  imported: number;
  failed: number;
  results: HeadshotImportResult[];
}
